import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../services/api';
import { 
  Modal, 
  Button, 
  Table, 
  Pagination, 
  Input, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Select, 
  Space, 
  message,
  Popconfirm,
  Tag
} from 'antd';
import { 
  SearchOutlined, 
  DeleteOutlined, 
  PlusOutlined, 
  UserOutlined 
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const IpAllocation = () => {
  const [ips, setIps] = useState([]);
  const [username, setUsername] = useState('');
  const [ip, setIp] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]); // 可分配的用户列表
  const [userSearchTerm, setUserSearchTerm] = useState(''); // 用户搜索关键词
  const [searchTerm, setSearchTerm] = useState(''); // 实际搜索条件
  const [inputValue, setInputValue] = useState(''); // 输入框的值
  const [showModal, setShowModal] = useState(false);
  const [selectedIps, setSelectedIps] = useState(new Set());
  const [page, setPage] = useState(1);

  const [totalIps, setTotalIps] = useState(0);
  const [limit] = useState(10);

  const searchTimeoutRef = useRef(null); // 用于防抖的引用

  // 格式化账号类型显示
  const formatAccountType = (accountType) => {
    const typeMap = {
      'user': '普通用户',
      'wlj-user': '物理机用户',
      'admin': '管理员'
    };
    return typeMap[accountType] || accountType;
  };

  // 格式化IP类型显示
  const formatIpType = (ipType) => {
    const typeMap = {
      'vps': { text: 'VPS', color: 'blue' },
      'physical': { text: '物理机', color: 'green' },
      'colocation': { text: '托管', color: 'orange' },
      'manual': { text: '人工', color: 'purple' }
    };
    return typeMap[ipType] || { text: ipType || 'VPS', color: 'default' };
  };

  // 获取可分配的用户列表（非管理员用户）
  const fetchAvailableUsers = useCallback(async (search = '') => {
    try {
      const response = await API.get('/api/admin/non-admin-users', {
        params: { search }
      });
      setAvailableUsers(response.data.usernames || []);
    } catch (error) {
      console.error('获取用户列表失败:', error);
      showAlert('获取用户列表失败', 'danger');
    }
  }, []);

  // 获取所有 IP 地址
  const fetchIps = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/ips', {
        params: { search: searchTerm, page, limit },
      });
      setIps(response.data.ips);
      setTotalIps(response.data.totalIps);
      setSelectedIps(new Set());
    } catch (error) {
      showAlert('获取IP列表失败', 'danger');
    }
  }, [searchTerm, page, limit]);

  // 防抖搜索用户
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      fetchAvailableUsers(userSearchTerm);
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [userSearchTerm, fetchAvailableUsers]);

  useEffect(() => {
    fetchIps();
    fetchAvailableUsers(); // 组件加载时获取用户列表
  }, [fetchIps, fetchAvailableUsers]);

  // 处理搜索输入变化 - 只更新输入框的值，不触发搜索
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // 处理搜索按钮点击 - 触发实际搜索
  const handleSearch = () => {
    setSearchTerm(inputValue);
    setPage(1); // 搜索时重置为第一页
  };



  // 处理用户搜索
  const handleUserSearch = (e) => {
    setUserSearchTerm(e.target.value);
  };

  // 分配IP
  const handleAssignIp = async () => {
    try {
      const response = await API.put('/api/admin/assign-ip', {
        username,
        ip,
      });

      const { alreadyAssignedIps, successfullyAssignedIps } = response.data;

      if (alreadyAssignedIps && alreadyAssignedIps.length > 0) {
        const alreadyAssignedIpsList = alreadyAssignedIps.join(', ');
        const successMessage = successfullyAssignedIps && successfullyAssignedIps.length > 0
          ? `成功分配的IP: ${successfullyAssignedIps.join(', ')}`
          : '';

        showAlert(`以下IP已被分配: ${alreadyAssignedIpsList}。${successMessage}`, 'warning');
      } else {
        showAlert('IP分配成功', 'success');
      }

      setShowModal(false);
      setUsername('');
      setIp('');
      await fetchIps();
    } catch (error) {
      const errorMessage = error.response?.data?.message || '分配IP失败';
      showAlert(errorMessage, 'danger');
    }
  };

  // 删除单个IP
  const handleDeleteIp = async (ipId) => {
    try {
      await API.delete(`/api/admin/ips/${ipId}`);
      showAlert('IP删除成功', 'success');
      await fetchIps();
    } catch (error) {
      showAlert('删除IP失败', 'danger');
    }
  };

  // 批量删除IP
  const handleBulkDelete = async () => {
    const selectedIpsArray = Array.from(selectedIps);
    if (selectedIpsArray.length === 0) {
      showAlert('请选择要删除的IP', 'warning');
      return;
    }

    try {
      // 获取选中IP的ID数组
      const selectedIpIds = ips
        .filter(ipObj => selectedIps.has(ipObj.ip))
        .map(ipObj => ipObj._id);
      
      await API.delete('/api/admin/ips', { data: { ips: selectedIpIds } });
      showAlert('批量删除成功', 'success');
      setSelectedIps(new Set());
      
      // 检查是否删除了当前页的所有数据
      const remainingIpsOnCurrentPage = ips.length - selectedIpsArray.length;
      
      // 如果当前页没有剩余数据且不是第一页，跳转到前一页
      if (remainingIpsOnCurrentPage === 0 && page > 1) {
        setPage(page - 1);
      } else {
        // 否则重新获取当前页数据
        await fetchIps();
      }
    } catch (error) {
      showAlert('批量删除失败', 'danger');
    }
  };

  // 显示提示信息
  const showAlert = (text, variant) => {
    if (variant === 'success') {
      message.success(text);
    } else if (variant === 'danger') {
      message.error(text);
    } else if (variant === 'warning') {
      message.warning(text);
    } else {
      message.info(text);
    }
  };

  // 打开分配IP模态框时重置表单
  const handleShowModal = () => {
    setUsername('');
    setIp('');
    setUserSearchTerm('');
    fetchAvailableUsers(''); // 重置用户列表
    setShowModal(true);
  };



  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>IP 分配</Title>
      
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle" gutter={16}>
          <Col>
            <Text type="secondary">
              总IP数量：<Text strong>{totalIps}</Text>
            </Text>
          </Col>
          <Col flex="1" style={{ maxWidth: 400 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="搜索用户名或IP"
                value={inputValue}
                onChange={handleInputChange}
                onPressEnter={handleSearch}
                suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
              />
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
            </Space.Compact>
          </Col>
        </Row>
      </Card>

      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>IP列表</Title>
          </Col>
          <Col>
            <Space>
              <Button 
                danger 
                icon={<DeleteOutlined />}
                onClick={handleBulkDelete}
              >
                批量删除
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleShowModal}
              >
                分配 IP
              </Button>
            </Space>
          </Col>
        </Row>

              <Table
          dataSource={ips}
          rowKey="_id"
          pagination={false}
          rowSelection={{
            selectedRowKeys: ips.filter(ip => selectedIps.has(ip.ip)).map(ip => ip._id),
            onChange: (selectedRowKeys) => {
              const selectedIpAddresses = ips
                .filter(ip => selectedRowKeys.includes(ip._id))
                .map(ip => ip.ip);
              setSelectedIps(new Set(selectedIpAddresses));
            },
            getCheckboxProps: (record) => ({
              name: record.ip,
            }),
          }}
          columns={[
            {
              title: 'IP 地址',
              dataIndex: 'ip',
              key: 'ip',
              align: 'center',
              width: 150,
            },
            {
              title: 'IP类型',
              key: 'ipType',
              align: 'center',
              width: 100,
              render: (_, record) => {
                const typeConfig = formatIpType(record.ipType);
                return (
                  <Tag color={typeConfig.color}>
                    {typeConfig.text}
                  </Tag>
                );
              },
            },
            {
              title: '主账号',
              key: 'assignedTo',
              align: 'center',
              width: 120,
              render: (_, record) => (
                record.assignedTo ? record.assignedTo.username : '未分配'
              ),
            },
            {
              title: 'RAM账号',
              key: 'sharedWith',
              align: 'center',
              width: 200,
              render: (_, record) => {
                if (record.sharedWith && record.sharedWith.length > 0) {
                  return (
                    <div>
                      {record.sharedWith.map((user) => (
                        <div key={user._id} style={{ marginBottom: 4 }}>
                          {user.username}
                        </div>
                      ))}
                    </div>
                  );
                } else {
                  return '无共享用户';
                }
              },
            },
            {
              title: '操作',
              key: 'action',
              align: 'center',
              width: 100,
              render: (_, record) => (
                <Popconfirm
                  title="确定要删除这个IP吗？"
                  onConfirm={() => handleDeleteIp(record._id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button danger size="small" icon={<DeleteOutlined />}>
                    删除
                  </Button>
                </Popconfirm>
              ),
            },
          ]}
        />
      </Card>

      {/* 分配 IP 的弹窗 */}
      <Modal
        title="分配 IP"
        open={showModal}
        onOk={handleAssignIp}
        onCancel={() => setShowModal(false)}
        okText="分配"
        cancelText="关闭"
        width={600}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>选择用户</label>
            <div style={{ marginBottom: 8 }}>
              <Input
                placeholder="搜索用户名..."
                value={userSearchTerm}
                onChange={handleUserSearch}
                prefix={<UserOutlined />}
              />
            </div>
            <Select
              value={username}
              onChange={setUsername}
              style={{ width: '100%' }}
              placeholder="请选择用户"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {availableUsers.map((user) => (
                <Option key={user.username} value={user.username}>
                  {user.username} ({formatAccountType(user.accountType || user.role)})
                </Option>
              ))}
            </Select>
            {availableUsers.length === 0 && userSearchTerm && (
              <Text type="secondary" style={{ fontSize: 12 }}>未找到匹配的用户</Text>
            )}
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>IP 地址</label>
            <TextArea
              rows={5}
              placeholder="每行一个IP或IP范围"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* 分页控件 */}
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Pagination
          current={page}
          total={totalIps}
          pageSize={limit}
          onChange={setPage}
          showSizeChanger={false}
          showQuickJumper
          showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`}
        />
      </div>
    </div>
  );
};

export default IpAllocation;
